import { FC, useRef, useState } from 'react';

import parsePhoneNumberFromString from 'libphonenumber-js';
import { twMerge } from 'tailwind-merge';

import { CheckIcon } from '@/shared/assets';
import { ICountry, cn } from '@/shared/lib';
import { Flag, Input, Skeleton } from '@/shared/ui';

import { formatPhoneNumber, unFormatPhoneNumber } from '../../lib';
import { CountrySelect } from '../country-select';
import { IPhoneNumberInput } from './types';

export const PhoneNumberInput: FC<IPhoneNumberInput> = ({
  className,
  onChangeCountry,
  placeholder = 'Phone',
  value,
  onValueChange,
  isInvalid,
  disabled,
  defaultCountryCode,
  description,
  variant,
  size,
  initialPartValue,
  noPlaceholder,
  ...props
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const isManualInput = useRef<boolean>(false);
  const [country, setCountry] = useState<ICountry | null>(null);
  const [partPhoneNumber, setPartPhoneNumber] = useState(() => {
    if (initialPartValue) return initialPartValue;
    if (!value || !country?.phoneCode) return '';

    const phoneCode = `+${country.phoneCode}`;
    if (value.startsWith(phoneCode)) {
      return value.substring(phoneCode.length);
    }

    return value.replace(/\D/g, '');
  });

  const selectCountryHandler = (country: ICountry | null) => {
    setCountry(country);
    onChangeCountry?.(country ?? null);
    if (!initialPartValue || partPhoneNumber !== initialPartValue) {
      setPartPhoneNumber('');
      onValueChange('', '');
    }
  };

  const checkAutoCompleteNumber = (value: string) => {
    const phone = parsePhoneNumberFromString(value);
    if (phone?.isValid() && phone.country) {
      const formattedNumber = phone.formatInternational();
      const newCountry: ICountry = {
        id: '',
        name: '',
        code: phone.country,
        iso: phone.country,
        phoneCode: phone.countryCallingCode.toString(),
        mask: formattedNumber ? formattedNumber.replace(/\d/g, '#') : '',
        alpha3: '',
      };

      setCountry(newCountry);
      onChangeCountry?.(newCountry);

      const nationalNumber = phone.nationalNumber || '';
      const formattedValue = formatPhoneNumber(nationalNumber, newCountry);
      setPartPhoneNumber(formattedValue);

      if (nationalNumber?.trim() && newCountry?.phoneCode) {
        const cleanValue = nationalNumber.replace(/\D/g, '');
        onValueChange(
          unFormatPhoneNumber(`${newCountry.phoneCode}${cleanValue}`),
          formattedValue,
        );
      }
      return true;
    }
    return false;
  };

  const changePhoneNumberHandler = (value: string) => {
    if (!isManualInput.current) {
      const isAutoComplete = checkAutoCompleteNumber(value);
      if (isAutoComplete) {
        return;
      }
    }
    isManualInput.current = false;

    const formattedValue = formatPhoneNumber(value, country);
    setPartPhoneNumber(formattedValue);

    if (!value?.trim()) {
      onValueChange('', '');
      return;
    }

    if (value?.trim() && country?.phoneCode) {
      const cleanValue = value.replace(/\D/g, '');
      onValueChange(
        unFormatPhoneNumber(`${country.phoneCode}${cleanValue}`),
        formattedValue,
      );
    }
  };

  const handlerPasteHandler = (
    event: React.ClipboardEvent<HTMLInputElement>,
  ) => {
    event.preventDefault();
    const pastedValue = event.clipboardData.getData('text');
    const isAutoComplete = checkAutoCompleteNumber(pastedValue);

    if (!isAutoComplete) {
      changePhoneNumberHandler(pastedValue);
    }
  };

  return (
    <div className={cn('flex gap-x-[14px]', className)}>
      <CountrySelect
        modalKey="phone-number-input-country-select"
        prefixQueryKey="phone-number-input"
        defaultCountry={defaultCountryCode || 'CA'}
        value={country}
        isValueCode
        onSelect={selectCountryHandler}
        isInvalid={isInvalid}
        disabled={disabled}
        isPhoneNumber
        triggerProps={{
          variant,
          size,
          className: 'w-max min-w-[60px] shrink-0',
        }}
        classNames={{
          trigger: 'mb-0',
        }}
        listProps={{
          size: 'sm',
        }}
        CustomSkeletons={
          <div className="relative flex w-full items-center gap-4">
            <Skeleton className="size-10 rounded-full" />
            <Skeleton className="h-5 w-8" />
            <Skeleton className="absolute left-[112px] top-1/2 h-5 w-[100px] -translate-y-1/2" />
          </div>
        }
        renderOptionItem={(item) => (
          <div className="relative flex w-full items-center gap-4">
            <div className="relative size-10">
              <Flag
                alpha2={item.option?.code ?? ''}
                className="h-full w-full"
              />
              {country && item.value === country.id && (
                <div className="absolute -bottom-0.5 -right-0.5 size-[19px] rounded-full border-[3px] border-base-500 bg-icon-base-500">
                  <CheckIcon className="size-full text-icon-contrast-500" />
                </div>
              )}
            </div>
            {item.option?.phoneCode && (
              <p className="text-base-300 typography-16r">{`+${item.option.phoneCode}`}</p>
            )}

            {item.option?.name && (
              <p className="absolute left-[112px] top-1/2 -translate-y-1/2 typography-16r">
                {item.option.name}
              </p>
            )}
          </div>
        )}
        renderTriggerValue={(value) => (
          <div className="flex items-center gap-2">
            <Flag
              alpha2={value?.option?.code ?? ''}
              className="h-6 w-6 max-w-6"
            />
            {value?.option?.phoneCode && (
              <p>{`+${value.option.phoneCode ?? ''}`}</p>
            )}
          </div>
        )}
      />
      <Input
        ref={inputRef}
        value={partPhoneNumber}
        onPaste={handlerPasteHandler}
        disabled={!country || disabled}
        isInvalid={isInvalid}
        onValueChange={changePhoneNumberHandler}
        className={twMerge('flex-1', noPlaceholder && '!rounded-[16px]')}
        type="tel"
        onKeyDown={(event) => {
          if (event.key === 'Backspace' && !partPhoneNumber.trim()) {
            return;
          }
          isManualInput.current = true;
        }}
        autoComplete="tel-national"
        description={description}
        placeholder={noPlaceholder ? '' : placeholder}
        variant={variant}
        size={size}
        classNames={{
          input: 'w-full',
          placeholder: noPlaceholder ? 'hidden' : '',
        }}
        {...(props as Omit<typeof props, 'size'>)}
      />
    </div>
  );
};
